import { useGoogleLogin } from '@react-oauth/google'
import { drfFetch, LoadingEllipsis, login, useStore, useRouter } from '@/core'
import { environment } from '@/consts'
import { fetchUserInfo } from '@u/drf/auth'
import { legacyRoute } from '@u/legacy-route'
import { CompanyDoesNotExistModal } from '@c/onboarding-app/company-does-not-exist-modal'
import { doc } from './doc'
import { useTranslation } from 'react-i18next'
import styles from './style.module.styl'

const GoogleIcon = () => (
  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg">
    <g fill="#000" fillRule="evenodd">
      <path
        d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"
        fill="#EA4335"
      />
      <path
        d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"
        fill="#4285F4"
      />
      <path
        d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"
        fill="#FBBC05"
      />
      <path
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z"
        fill="#34A853"
      />
      <path fill="none" d="M0 0h18v18H0z" />
    </g>
  </svg>
)

export const GoogleButton = doc(({ children }) => {
  const { t } = useTranslation()
  const store = useStore()
  const router = useRouter()
  const [modal, setModal] = store.use(['modal'])
  const [next] = store.use(['next'])
  const [avatar, setAvatar] = store.use(['sso', 'avatar'], '')

  const handleRouting = (info) => {
    if (next.route) {
      return router.push(next.route)
    }
    if (next.string) {
      return legacyRoute(next.string)
    }
    if (info.legacyNextUrl) {
      return legacyRoute(info.legacyNextUrl)
    }
    if (info.nextUrl) {
      return router.push({ name: info.nextUrl })
    }
    if (info.legacyHomeUrl) {
      return legacyRoute(info.legacyHomeUrl)
    }
    return router.push({ name: info.homeUrlName })
  }

  const googleLogin = useGoogleLogin({
    onSuccess: async (res) => {
      try {
        const loginRes = await drfFetch('googleLogin', {
          method: 'POST',
          body: {
            code: res.code,
            isNonprofitAccount: false,
          },
        })

        loginRes?.avatarContent && setAvatar(loginRes.avatarContent)

        const info = await fetchUserInfo(loginRes)

        await login(store, {
          token: loginRes.token,
          info,
        })
        loginRes?.avatarContent && setAvatar(loginRes.avatarContent)
        handleRouting(info)
      } catch (err) {
        if (
          err.fieldErrors?.email?.includes('Company does not exist') ||
          err.fieldErrors?.email?.includes(
            t('auth.signupWithEmailPage.errors.company_not_exist')
          )
        ) {
          setModal('company-does-not-exist')
          console.error(
            'Company does not exist error:',
            JSON.stringify(err, null, 2)
          )
          return
        }
        console.error(t('auth.googleButton.errors.login_failed'), err)
      }
    },
    flow: 'auth-code',
    ux_mode: 'popup',
    redirect_uri: 'postmessage',
    onError: (error) =>
      console.error(t('auth.googleButton.errors.login_failed'), error),
    onNonOAuthError: (error) => {
      console.error('Google non-OAuth error:', error)
    },
  })

  return (
    <>
      <CompanyDoesNotExistModal />

      <button
        className={styles.google}
        onClick={() => {
          try {
            // Add a small delay to ensure proper user activation
            setTimeout(() => {
              googleLogin()
            }, 100)
          } catch (e) {
            console.error('Error in Google login flow:', e)
          }
        }}
      >
        {' '}
        <div className={styles.googleIconWrapper}>
          <GoogleIcon />
        </div>
        {t('auth.googleButton.text')}
      </button>
    </>
  )
})
